import merge from "lodash/merge";
import { createTheme, responsiveFontSizes } from "@mui/material";
import { THEMES } from "./constants";
import { lightShadows, darkShadows } from "./shadows";

const baseOptions = {
  direction: "ltr",
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input[type=color]': {
            cursor: 'pointer',
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: "75%",
          width: "75%",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          height: "100%",
          width: "100%",
        },
        body: {
          height: "100%",
        },
        "#root": {
          height: "100%",
        },
        "#nprogress .bar": {
          zIndex: "2000 !important",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: "hidden",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "auto",
          marginRight: "16px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          '@media (max-width: 900px)': {
            alignItems: 'flex-start',
          },
        },
        paperFullScreen: {
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          maxWidth: '100%',
          margin: 0,
          borderRadius: 0,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: "pointer",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ theme }) => {
          const thumb = theme.palette.scrollbarThumb;
          const track = theme.palette.scrollbarBackground;
          const thumbHover =
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.45)"
              : "rgba(0, 0, 0, 0.35)";
          return {
            scrollbarWidth: "thin",
            scrollbarColor: `${thumb} ${track}`,
            "&::-webkit-scrollbar": { width: 8, height: 8 },
            "&::-webkit-scrollbar-track": { backgroundColor: track },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: thumb,
              borderRadius: 4,
              border: `1px solid ${track}`,
            },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: thumbHover },
          };
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: "3.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "3rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "2.25rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    overline: {
      fontWeight: 600,
    },
  },
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              opacity: 0.86,
              color: "#42526e",
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontWeight: "bold",
          },
        },
      },
    },
    palette: {
      topNavSvg: '#5b5b5b',
      iosMoveSVG: '#5b5b5b',
      basicBlackWhite: 'black',
      chartColors: [
        '#FF6E6E',
        '#938AE1',
        '#76C96D',
        '#6BA7F5',
        '#FE9E21',
        '#D567B4',
        '#0DAA94',
        '#CACE4C',
        '#B96161',
        '#D1AC77',
        '#7FCEE2',
        '#B986EC',
        '#B81955',
        '#B9D653',
        '#CF30AF'
      ],
      dashboardWarning: {
        high: {
          main: '#D22626',
          contrastText: "#ffffff",
          svgColor: "#ee404c"
        },
        medium: {
          main: '#e3750a',
          contrastText: "#ffffff",
          svgColor: "#ffa30b"
        },
        low: {
          main: '#e3c500',
          contrastText: "#ffffff",
          svgColor: "#ffee00"
        },
        info: {
          main: '#569965',
          contrastText: '#ffffff',
          svgColor: '#ffffff'
        }
      },
      card: {
        background: '#f4f5f7'
      },
      chart: {
        even: "#7648a3",
        odd: "#b493be",
      },
      white: "#fff",
      darkGrey: "#5B5B5B",
      border: "#BDBDBD",
      disabled: "#d6d7d9",
      overlay: "rgba(0, 0, 0, 0.50)",
      editButtonBackground: "rgba(0, 0, 0, 0.50)",
      imageEditButtonBackground: "rgba(255, 255, 255, 0.9)",
      imageEditButtonHoverBackground: "rgba(255, 255, 255, 1)",
      boxShadow: "#6b778c",
      box: "#663e83",
      secondaryTitle: "#6b778c",
      svg: "#5b5b5b",
      // delete: "#d95c5c",
      delete: "#BD3737",
      autoMessages: {
        promoBoxBackground: 'rgba(235, 66, 66, 0.2)',
        promoCode: '#EB4242 '
      },
      sendType: {
        sms: '#E3C500',
        email: '#1CB1DE',
        notification: '#E3750A',
        whatsapp: '#83BD37'
      },
      selected: {
        chip: "#86469a",
      },
      autocompleteChip: "#E2E2E2",
      chefsNoteBackground: "#fefce8",
      chefsNoteBorder: "#fdc700",
      cardGradientStart: "#faf5ff",
      cardGradientEnd: "#ddd6fe",
      previewBackground: "#F2F2F2",
      cardShadow: "#E2E2E2",
      buttonShadow: "rgba(0, 0, 0, 0.3)",
      skeleton: {
        light: "#dcdee0",
        dark: "#c8cdd3",
      },
      descriptionItemBackground: "#eaebed",
      iconPreviewBackground: "#dfecd0",
      iconOverlay: "rgba(255, 255, 255, 0.2)",
      iconButtonShadow: "grey",
      unselectedTabBackground: "#f4f5f7",
      dateIconBackground: "#ddcfe2",
      editingOverlay: "rgba(200, 200, 200, 0.5)",
      scrollbarBackground: "rgba(0, 0, 0, 0.05)",
      scrollbarThumb: "rgba(0, 0, 0, 0.2)",
      avatar: {
        fallbackBackground: "#d8d8d8",
      },
      shadow: {
        input: "0px 1px 2px rgba(0, 0, 0, 0.12),0px 0px 0px 1px rgba(0, 0, 0, 0.05)",
      },
      cancelButton: {
        main: "#757575",
        hover: "#616161",
      },
      reportTabs: '#EEEEEE',
      switch: {
        thumb: {
          light: "#ffffff",
          dark: "#bdbdbd",
        },
        track: {
          light: "#b7b7b7",
          dark: "#616161",
        },
        focusVisible: {
          light: "#33cf4d",
          dark: "#4caf50",
        },
        focusBorder: {
          light: "#ffffff",
          dark: "#222b36",
        },
      },
      orderStatus: {
        isPaid: "#1FB719",
        notPaid: "#EB4242",
        canceledBackground: "rgba(255, 87, 0, 0.28)",
        delayedBackground: "rgba(255, 0, 0, 0.18)",
        rejectedBackground: "rgba(192, 192, 192, 0.5)",
      },
      userMenu: {
        background: "#f4f5f7",
        icons: "#8130cb",
      },
      messages: {
        background: "#f8f8f8",
        headerBackground: "#f3f3f3",
      },
      pageTitle: "#5b5b5b",
      rateStar: "#97c24c",
      navItems: {
        active: {
          subText: "#723087",
          text: "#000",
          arrow: "black",
          background: "rgba(0, 0, 0, 0.04)"
        },
        default: {
          text: "#212B36",
          background: "transparent",
          hover: "rgba(0, 0, 0, 0.04)"
        },
        arrow: "black",
      },
      menuButton: {
        activeBackgroundColor: "#dadada",
        backgroundColor: "#f4f5f7",
      },
      disabledSearch: {
        background: "#e8e8e8",
      },
      titleBox: {
        contrastText: "#ffffff",
        backgroundColor: "#723087",
      },
      action: {
        active: "#6b778c",
      },
      background: {
        default: "#f4f5f7",
        paper: "#ffffff",
        hover: "#e5dee7",
        selected: "#eee3f6",
        chipSelected: "#c0a9c6",
        tableDropdown: "#c5c5c5",
        tableEven: "#eaebed",
        tableOdd: "#f4f5f7",
        shape: '#B493BE72',
      },
      activity: {
        isActive: "#7648a3",
        isInactive: "#D1D1D1",
        isAway: "#FFBD08",
      },
      sizeColors: [
        { main: '#9C27B0', light: '#F3E5F5' }, // Purple
        { main: '#2196F3', light: '#E3F2FD' }, // Blue
        { main: '#4CAF50', light: '#E8F5E9' }, // Green
        { main: '#FF9800', light: '#FFF3E0' }, // Orange
        { main: '#F44336', light: '#FFEBEE' }, // Red
        { main: '#00BCD4', light: '#E0F7FA' }, // Cyan
      ],
      error: {
        contrastText: "#ffffff",
        main: "#ff0000",
        light: '#ffcccc'
      },
      secondary: {
        contrastText: "#ffffff",
        main: "#5b5b5b",
        light: '#9D9D9D'
      },
      whiteButton: {
        contrastText: "#5b5b5b",
        main: "#f5f5f5",
      },
      primaryContrast: {
        contrastText: "#172b4d",
        main: "#ffffff",
      },
      mode: "light",
      primary: {
        contrastText: "#ffffff",
        main: "#723087",
        light: '#f4e1fe',
      },
      secondaryTable: {
        contrastText: "#ffffff",
        main: "#cecece",
        mainLight: '#b493be'
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
        light: '#e8f5e9',
        dark: '#2e7d32',
      },
      info: {
        contrastText: "#ffffff",
        main: "#2196f3",
        light: '#e3f2fd',
        dark: '#1565c0',
      },
      text: {
        primary: "#172b4d",
        secondary: "#6b778c",
        third: '#000'
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: '#fff3e0',
        dark: '#e65100',
      },
      warningAlert: {
        background: 'rgba(255, 152, 0, 0.08)',
        border: 'rgba(255, 152, 0, 0.25)',
        hoverBackground: 'rgba(255, 152, 0, 0.12)',
        buttonBorder: 'rgba(255, 152, 0, 0.4)',
        buttonHoverBackground: 'rgba(255, 152, 0, 0.1)',
      },
      neoChip: {
        background: 'transparent',
        border: 'rgba(114, 48, 135, 0.3)',
        color: '#723087',
        hoverBackground: 'rgba(114, 48, 135, 0.08)',
        hoverBorder: 'rgba(114, 48, 135, 0.5)',
        boxShadow: '0 2px 8px rgba(114, 48, 135, 0.1)',
      },
      puomCard: {
        background: '#f5f5f5',
        text: '#172b4d',
        secondaryText: '#6b778c',
        readonlyField: 'rgba(255,255,255,0.7)',
        readonlyText: '#6b778c',
        readonlyBorder: '#e0e0e0',
        closeButton: 'rgba(186, 186, 186, 0.8)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      },
      suomCard: {
        background: '#E6E6FA',
        text: '#172b4d',
        secondaryText: '#6b778c',
        readonlyField: 'rgba(255,255,255,0.7)',
        readonlyText: '#6b778c',
        readonlyBorder: '#e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      },
      purchasedProductCard: {
        supplierNameBackground: '#f5f5f5',
        offerBackground: '#FFF3CD',
        offerText: '#F57C00',
        offerIcon: '#F57C00',
        supplierCardColors: [
          '#f0f8ff', '#fff0f5', '#f0fff0', '#fff8dc', '#f5f5f5', 
          '#fffaf0', '#e6e6fa', '#ffe4e1', '#fafad2', '#e0ffff', 
          '#f5fffa', '#fdf5e6', '#f8f8ff', '#faf0e6', '#fffacd', '#f0e68c'
        ],
      },
      selectedProductBadge: {
        background: '#f4e1fe',
        border: 'rgba(114, 48, 135, 0.25)',
        text: '#172b4d',
      },
      recipeBook: {
        headerGradientStart: '#faf5ff',
        headerGradientEnd: '#ddd6fe',
        iconBoxBackground: '#f3e8ff',
        iconColor: '#9333ea',
        labelText: '#4b5563',
        valueText: '#1f2937',
      },
      difficultyLevel: {
        easy: {
          background: '#d1fae5',
          color: '#065f46',
          border: '#10b981',
        },
        medium: {
          background: '#fef3c7',
          color: '#78350f',
          border: '#fbbf24',
        },
        hard: {
          background: '#fee2e2',
          color: '#991b1b',
          border: '#ef4444',
        },
        default: {
          background: '#f3f4f6',
          color: '#374151',
          border: '#d1d5db',
        },
      },
      sidebarSelectedBackground: '#F5F5F4',
      sidebar: {
        activeText: '#212B36',
        inactiveText: '#666666',
        secondaryText: '#637381',
        scrollbar: '#cccccc',
        divider: 'rgba(102, 102, 102, 0.2)',
        boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.35)',
        dropdownShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        icon: '#727272',
        logout: "#d55f5a",
        text: "#757575"
      },
      dashboardTabs: {
        backgroundColor: '#e0e0e0',
        textColor: '#212B36',
        hoverBackgroundColor: '#d5d5d5',
      },
      segmentedControl: {
        container: '#e0e0e0',
        activeBackground: '#ffffff',
        activeText: '#212B36',
        inactiveText: '#637381',
        hoverBackground: 'rgba(0, 0, 0, 0.04)',
      },
      pricingLevels: {
        inputBorder: '#BDBDBD',
        inputBackground: '#ffffff',
        inputUnselectedBackground: 'rgba(0, 0, 0, 0.02)',
        cardShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        cardHoverShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        cardDisabledShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
        chipBackground: '#f4e1fe',
        searchBarBackground: '#f5f5f5',
      },
      link: {
        main: '#0d47a1',
        hover: '#1565c0',
      },
      salesCards: {
        revenue: {
          background: '#F0FDF4',
          border: '#BBF7D0',
          icon: '#10B981',
          text: '#10B981',
        },
        orders: {
          background: '#EFF6FF',
          border: '#BFDBFE',
          icon: '#3B82F6',
          text: '#3B82F6',
        },
        customers: {
          background: '#FAF5FF',
          border: '#E9D5FF',
          icon: '#8B5CF6',
          text: '#8B5CF6',
        },
        avgOrder: {
          background: '#FFF7ED',
          border: '#FED7AA',
          icon: '#F59E0B',
          text: '#F59E0B',
        },
        discount: {
          background: '#FEE2E2',
          border: '#FECACA',
          icon: '#EF4444',
          text: '#EF4444',
        },
      },
      virtualLocation: {
        main: '#7c3aed',
        dark: '#6d28d9',
        light: 'rgba(124, 58, 237, 0.04)',
        hover: 'rgba(124, 58, 237, 0.08)',
        selected: 'rgba(124, 58, 237, 0.04)',
      },
    },
    shadows: lightShadows,
  },
  [THEMES.DARK]: {
    components: {
      MuiTableHead: {
        root: {
          borderRadius: 5,
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      topNavSvg: 'white',
      iosMoveSVG: 'white',
      basicBlackWhite: 'white',
      chartColors: [
        '#E81818',
        '#E28000',
        '#4ED040',
        '#40D0BC',
        '#6456E2',
        '#D040A5',
        '#0A74FF',
        '#9DA200',
        '#D28080',
        '#B87819',
        '#00B2DF',
        '#9933FF',
        '#EC699A',
        '#E3BE00',
        '#F29B17'
      ],
      dashboardWarning: {
        high: {
          main: '#D22626',
          contrastText: "#ffffff",
          svgColor: "#ee404c"
        },
        medium: {
          main: '#e3750a',
          contrastText: "#ffffff",
          svgColor: "#ffa30b"
        },
        low: {
          main: '#e3c500',
          contrastText: "#ffffff",
          svgColor: "#ffee00"
        },
        info: {
          main: '#569965',
          contrastText: '#ffffff',
          svgColor: '#ffffff'
        }
      },
      card: {
        background: '#222b36'
      },
      chart: {
        even: "#723087",
        odd: "#8b6ba3",
      },
      white: "#ffffff",
      darkGrey: "#919eab",
      border: "rgba(145, 158, 171, 0.24)",
      disabled: "#4a5568",
      overlay: "rgba(0, 0, 0, 0.70)",
      editButtonBackground: "rgba(255, 255, 255, 0.2)",
      imageEditButtonBackground: "rgba(0, 0, 0, 0.7)",
      imageEditButtonHoverBackground: "rgba(0, 0, 0, 0.85)",
      boxShadow: "#919eab",
      box: "#b975e0",
      secondaryTitle: "#919eab",
      svg: "#919eab",
      delete: "#f44336",
      autoMessages: {
        promoBoxBackground: 'rgba(235, 66, 66, 0.3)',
        promoCode: '#EB4242 '
      },
      sendType: {
        sms: '#E3C500',
        email: '#1CB1DE',
        notification: '#E3750A',
        whatsapp: '#83BD37'
      },
      selected: {
        chip: "#723087",
      },
      autocompleteChip: "rgba(255, 255, 255, 0.1)",
      chefsNoteBackground: "#4A3A1F",
      chefsNoteBorder: "#FFC107",
      cardGradientStart: "#2D1B3D",
      cardGradientEnd: "#4A2C5A",
      previewBackground: "rgba(255, 255, 255, 0.05)",
      cardShadow: "rgba(0, 0, 0, 0.2)",
      buttonShadow: "rgba(0, 0, 0, 0.5)",
      skeleton: {
        light: "rgba(255, 255, 255, 0.1)",
        dark: "rgba(255, 255, 255, 0.15)",
      },
      descriptionItemBackground: "rgba(255, 255, 255, 0.05)",
      iconPreviewBackground: "rgba(223, 236, 208, 0.2)",
      iconOverlay: "rgba(255, 255, 255, 0.1)",
      iconButtonShadow: "rgba(255, 255, 255, 0.1)",
      unselectedTabBackground: "rgba(255, 255, 255, 0.05)",
      dateIconBackground: "rgba(221, 207, 226, 0.3)",
      editingOverlay: "rgba(255, 255, 255, 0.1)",
      scrollbarBackground: "rgba(255, 255, 255, 0.1)",
      scrollbarThumb: "rgba(255, 255, 255, 0.3)",
      avatar: {
        fallbackBackground: "rgba(255, 255, 255, 0.1)",
      },
      shadow: {
        input: "0px 1px 2px rgba(0, 0, 0, 0.3), 0px 0px 0px 1px rgba(0, 0, 0, 0.1)",
      },
      cancelButton: {
        main: "#424242",
        hover: "#616161",
      },
      reportTabs: '#919eab',
      switch: {
        thumb: {
          light: "#ffffff",
          dark: "#bdbdbd",
        },
        track: {
          light: "#b7b7b7",
          dark: "#616161",
        },
        focusVisible: {
          light: "#33cf4d",
          dark: "#4caf50",
        },
        focusBorder: {
          light: "#ffffff",
          dark: "#222b36",
        },
      },
      orderStatus: {
        isPaid: "#4caf50",
        notPaid: "#f44336",
        canceledBackground: "rgba(255, 87, 0, 0.28)",
        delayedBackground: "rgba(255, 0, 0, 0.18)",
        rejectedBackground: "rgba(192, 192, 192, 0.5)",
      },
      userMenu: {
        background: "#222b36",
        icons: "#723087",
      },
      messages: {
        background: "#1e2530",
        headerBackground: "#2a3441",
      },
      pageTitle: "#ffffff",
      rateStar: "#ffc107",
      navItems: {
        active: {
          subText: "#723087",
          text: "#ffffff",
          arrow: "#ffffff",
          background: "rgba(114, 48, 135, 0.1)"
        },
        default: {
          text: "#919eab",
          background: "transparent",
          hover: "rgba(255, 255, 255, 0.05)"
        },
        arrow: "#919eab",
      },
      menuButton: {
        activeBackgroundColor: "#2a3441",
        backgroundColor: "#222b36",
      },
      disabledSearch: {
        background: "#2a3a4f",
      },
      titleBox: {
        contrastText: "#ffffff",
        backgroundColor: "#723087",
      },
      action: {
        active: "#919eab",
      },
      background: {
        default: "#171c24",
        paper: "#222b36",
        hover: "#2a3441",
        selected: "#1e2530",
        chipSelected: "#2a3441",
        tableDropdown: "#2a3441",
        tableEven: "#1e2530",
        tableOdd: "#222b36",
        shape: '#72308740',
      },
      activity: {
        isActive: "#723087",
        isInactive: "#4a5568",
        isAway: "#FFBD08",
      },
      sizeColors: [
        { main: '#BA68C8', light: '#4A148C' }, // Purple - brighter for dark mode
        { main: '#64B5F6', light: '#0D47A1' }, // Blue - brighter for dark mode
        { main: '#81C784', light: '#1B5E20' }, // Green - brighter for dark mode
        { main: '#FFB74D', light: '#E65100' }, // Orange - brighter for dark mode
        { main: '#E57373', light: '#B71C1C' }, // Red - brighter for dark mode
        { main: '#4DD0E1', light: '#006064' }, // Cyan - brighter for dark mode
      ],
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
        light: '#ffcccc',
      },
      secondary: {
        contrastText: "#ffffff",
        main: "#5b5b5b",
        light: '#9D9D9D'
      },
      whiteButton: {
        contrastText: "#ffffff",
        main: "#2a3441",
      },
      primaryContrast: {
        contrastText: "#ffffff",
        main: "#222b36",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#9463A4",
        light: '#f4e1fe',
      },
      secondaryTable: {
        contrastText: "#ffffff",
        main: "#4a5568",
        mainLight: '#723087'
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
        light: 'rgba(76, 175, 80, 0.15)',
        dark: '#2e7d32',
      },
      info: {
        contrastText: "#ffffff",
        main: "#2196f3",
        light: 'rgba(33, 150, 243, 0.15)',
        dark: '#1565c0',
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
        disabled: "#B4B4B4",
        third: '#ffffff'
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: 'rgba(255, 152, 0, 0.15)',
        dark: '#e65100',
      },
      warningAlert: {
        background: 'rgba(255, 152, 0, 0.08)',
        border: 'rgba(255, 152, 0, 0.25)',
        hoverBackground: 'rgba(255, 152, 0, 0.12)',
        buttonBorder: 'rgba(255, 152, 0, 0.4)',
        buttonHoverBackground: 'rgba(255, 152, 0, 0.1)',
      },
      neoChip: {
        background: 'transparent',
        border: 'rgba(148, 99, 164, 0.4)',
        color: '#9463A4',
        hoverBackground: 'rgba(148, 99, 164, 0.12)',
        hoverBorder: 'rgba(148, 99, 164, 0.6)',
        boxShadow: '0 2px 8px rgba(148, 99, 164, 0.2)',
      },
      puomCard: {
        background: '#2a3441',
        text: '#ffffff',
        secondaryText: '#b0b8c4',
        readonlyField: 'rgba(255,255,255,0.08)',
        readonlyText: '#919eab',
        readonlyBorder: 'rgba(145, 158, 171, 0.3)',
        closeButton: 'rgba(74, 85, 104, 0.9)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      },
      suomCard: {
        background: '#1e2530',
        text: '#ffffff',
        secondaryText: '#b0b8c4',
        readonlyField: 'rgba(255,255,255,0.08)',
        readonlyText: '#919eab',
        readonlyBorder: 'rgba(145, 158, 171, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      },
      purchasedProductCard: {
        supplierNameBackground: '#2a3441',
        offerBackground: 'rgba(255, 193, 7, 0.15)',
        offerText: '#FFC107',
        offerIcon: '#FFC107',
        supplierCardColors: [
          '#1e2530', '#2a1e2a', '#1e2a1e', '#2a281e', '#2a2a2a',
          '#2a241e', '#1e1a2a', '#2a1e1e', '#2a2a1e', '#1e2a2a',
          '#1e2a1e', '#2a251e', '#1e1e2a', '#2a1e1e', '#2a2a1e', '#2a2a1e'
        ],
      },
      selectedProductBadge: {
        background: 'rgba(148, 99, 164, 0.2)',
        border: 'rgba(148, 99, 164, 0.5)',
        text: '#ffffff',
      },
      recipeBook: {
        headerGradientStart: 'rgba(148, 99, 164, 0.15)',
        headerGradientEnd: 'rgba(148, 99, 164, 0.25)',
        iconBoxBackground: 'rgba(148, 99, 164, 0.2)',
        iconColor: '#9463A4',
        labelText: '#b0b8c4',
        valueText: '#ffffff',
      },
      difficultyLevel: {
        easy: {
          background: 'rgba(16, 185, 129, 0.2)',
          color: '#10b981',
          border: 'rgba(16, 185, 129, 0.4)',
        },
        medium: {
          background: 'rgba(251, 191, 36, 0.2)',
          color: '#fbbf24',
          border: 'rgba(251, 191, 36, 0.4)',
        },
        hard: {
          background: 'rgba(239, 68, 68, 0.2)',
          color: '#ef4444',
          border: 'rgba(239, 68, 68, 0.4)',
        },
        default: {
          background: 'rgba(145, 158, 171, 0.1)',
          color: '#919eab',
          border: 'rgba(145, 158, 171, 0.3)',
        },
      },
      sidebarSelectedBackground: '#2a3441',
      sidebar: {
        activeText: '#ffffff',
        inactiveText: '#919eab',
        secondaryText: '#b0b8c4',
        scrollbar: '#4a5568',
        divider: 'rgba(145, 158, 171, 0.2)',
        boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.5)',
        dropdownShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
        icon: 'white',
        logout: "#f44336",
        text: "#919eab"
      },
      dashboardTabs: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        textColor: '#ffffff',
        hoverBackgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      segmentedControl: {
        container: 'rgba(255, 255, 255, 0.05)',
        activeBackground: 'rgba(255, 255, 255, 0.1)',
        activeText: '#ffffff',
        inactiveText: '#919eab',
        hoverBackground: 'rgba(255, 255, 255, 0.08)',
      },
      pricingLevels: {
        inputBorder: '#9463A4',
        inputBackground: 'rgba(255, 255, 255, 0.05)',
        inputUnselectedBackground: 'rgba(255, 255, 255, 0.03)',
        cardShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        cardHoverShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
        cardDisabledShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
        chipBackground: 'rgba(148, 99, 164, 0.3)',
        searchBarBackground: 'rgba(255, 255, 255, 0.08)',
      },
      link: {
        main: '#64b5f6',
        hover: '#90caf9',
      },
      salesCards: {
        revenue: {
          background: 'rgba(16, 185, 129, 0.15)',
          border: 'rgba(16, 185, 129, 0.3)',
          icon: '#10B981',
          text: '#34D399',
        },
        orders: {
          background: 'rgba(59, 130, 246, 0.15)',
          border: 'rgba(59, 130, 246, 0.3)',
          icon: '#3B82F6',
          text: '#60A5FA',
        },
        customers: {
          background: 'rgba(139, 92, 246, 0.15)',
          border: 'rgba(139, 92, 246, 0.3)',
          icon: '#8B5CF6',
          text: '#A78BFA',
        },
        avgOrder: {
          background: 'rgba(245, 158, 11, 0.15)',
          border: 'rgba(245, 158, 11, 0.3)',
          icon: '#F59E0B',
          text: '#FBBF24',
        },
        discount: {
          background: 'rgba(239, 68, 68, 0.15)',
          border: 'rgba(239, 68, 68, 0.3)',
          icon: '#EF4444',
          text: '#F87171',
        },
      },
      virtualLocation: {
        main: '#9d7fce',
        dark: '#8b6ba3',
        light: 'rgba(157, 127, 206, 0.08)',
        hover: 'rgba(157, 127, 206, 0.12)',
        selected: 'rgba(157, 127, 206, 0.08)',
      },
    },
    shadows: darkShadows,
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      background: {
        default: "#1c2531",
        paper: "#293142",
      },
      divider: "rgba(145, 158, 171, 0.24)",
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#01ab56",
        light: "#7648A3",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
        light: 'rgba(255, 152, 0, 0.15)',
        dark: '#e65100',
      },
    },
    shadows: darkShadows,
  },
};

export const createCustomTheme = (config = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      // {
      //   ...(config.roundedCorners && {
      //     shape: {
      //       borderRadius: 16,
      //     },
      //   }),
      // },
      {
        direction: config.direction,
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
